const getSearchWordsWithIndex = (searchText) => {
  return searchText
    .toLowerCase()
    .split(' ')
    .filter((word) => word)
    .map((word, index) => ({
      word,
      index
    }));
};

const getSpanText = (spanElement) => {
  if (!spanElement) {
    return '';
  }

  return spanElement.textContent.toLowerCase();
};

const qualifyWordMatch = (searchWord, sequence) => {
  const THRESHOLD = 0.8;

  const searchWordLength = searchWord.length;
  const sequenceLength = sequence.length;

  if (sequenceLength === 0) {
    return {
      isMatch: false,
      matchScore: 0
    };
  }

  const matchScore = sequenceLength / searchWordLength;

  return {
    isMatch: matchScore >= THRESHOLD,
    matchScore: matchScore
  };
};

const getStartEnd = (str, sub) => {
  if (!sub) {
    return {
      start: -1,
      end: -1
    };
  }

  let start = str.indexOf(sub[0]); // Find the first character match
  if (start === -1) return { start: -1, end: -1 };

  let matchLength = 0;
  // Compare characters one by one until mismatch or end of either string
  while (
    matchLength < sub.length &&
    matchLength < str.length &&
    str[start + matchLength] === sub[matchLength]
  ) {
    matchLength++;
  }

  return {
    start,
    end: start + matchLength
  };
};

const getPossibleMatchesByWord = (spanElements, searchWord) => {
  const matches = [];

  let searchState = searchWord;
  let sequence = '';
  let spanToCharIdx = {};

  // Try all matches within spans
  for (const [spanIdx, spanElement] of spanElements.entries()) {
    const spanText = getSpanText(spanElement);
    const spanTextWords = spanText.split(' ');

    let currentPosition = 0; // Track position in the entire spanText

    for (const [spanWordIdx, spanWord] of spanTextWords.entries()) {
      if (sequence.length === 1) {
        // Reset state
        searchState = searchWord;
        sequence = '';
        spanToCharIdx = {};
      }

      const { start, end } = getStartEnd(spanWord, searchState);

      if (start === 0) {
        const matchText = spanWord.slice(start, end);
        searchState = searchState.slice(end);
        sequence += matchText;

        // Use currentPosition to calculate the actual start/end in the spanText
        const spanStart = currentPosition + start;
        const spanEnd = currentPosition + end;
        spanToCharIdx[spanIdx] = {
          start: spanStart,
          end: spanEnd,
          matchText
        };

        const { isMatch, matchScore } = qualifyWordMatch(searchWord, sequence);

        if (isMatch) {
          matches.push({
            matchText: sequence,
            location: spanToCharIdx,
            matchScore
          });

          // Reset state
          searchState = searchWord;
          sequence = '';
          spanToCharIdx = {};
        }
      }

      const isLastWord = spanWordIdx === spanTextWords.length - 1;

      // Update currentPosition to include the word length and space
      currentPosition += spanWord.length;

      if (!isLastWord) {
        currentPosition += 1; // Add 1 for the space between words

        // Reset state
        searchState = searchWord;
        sequence = '';
        spanToCharIdx = {};
      }
    }
  }

  return matches;
};

const getPossibleMatches = (spanElements, searchWords) => {
  const matchesByWordIdx = {};

  for (const { word, index } of searchWords) {
    matchesByWordIdx[index] = {
      word,
      matches: getPossibleMatchesByWord(spanElements, word)
    };
  }

  return matchesByWordIdx;
};

const getMatchDistance = (match1, match2, expectedOrder) => {
  const span1 = Number(Object.keys(match1.location)[0]);
  const span2 = Number(Object.keys(match2.location)[0]);

  let distance;
  if (span1 === span2) {
    const pos1 = match1.location[span1].end;
    const pos2 = match2.location[span2].start;
    distance = Math.abs(pos2 - pos1);
  } else {
    distance = Math.abs(span2 - span1) * 100;
  }

  // Much larger penalty for wrong order relative to search text
  if (
    (expectedOrder === 'after' && span2 < span1) ||
    (expectedOrder === 'after' &&
      span1 === span2 &&
      match2.location[span2].start < match1.location[span1].end)
  ) {
    distance += 1000000; // Much larger penalty for wrong order
  }

  return distance;
};

const findClosestMatch = (searchWordsWithIndex, matchesBySequence) => {
  const searchWordIndexes = searchWordsWithIndex
    .map((word) => word.index)
    .filter((i) => i !== 0);

  const firstWordMatches = matchesBySequence[0].matches;

  let bestSequence = null;
  let bestScore = Infinity;

  for (const firstMatch of firstWordMatches) {
    let currentSequence = [firstMatch];
    let lastMatch = firstMatch;
    let currentScore = 0;

    for (const wordIndex of searchWordIndexes) {
      const wordMatches = matchesBySequence[wordIndex].matches;
      let bestNextMatch = null;
      let bestDistance = Infinity;

      for (const match of wordMatches) {
        const distance = getMatchDistance(lastMatch, match, 'after');
        if (distance < bestDistance) {
          bestDistance = distance;
          bestNextMatch = match;
        }
      }

      if (bestNextMatch) {
        currentScore += bestDistance; // Add the distance to the current sequence score
        currentSequence.push(bestNextMatch);
        lastMatch = bestNextMatch;
      }
    }

    if (currentScore < bestScore) {
      // Compare current sequence score with best score
      bestScore = currentScore;
      bestSequence = currentSequence;
    }
  }

  return bestSequence || [];
};

const getHighlightPositions = (closestMatch) => {
  const highlightPositionsBySpanIdx = {};

  for (const match of closestMatch) {
    for (const spanIdx of Object.keys(match.location)) {
      const start = match.location[spanIdx].start;
      const end = match.location[spanIdx].end;
      if (!highlightPositionsBySpanIdx[spanIdx]) {
        highlightPositionsBySpanIdx[spanIdx] = {
          start,
          end
        };
      }

      if (highlightPositionsBySpanIdx[spanIdx]['start'] > start) {
        highlightPositionsBySpanIdx[spanIdx]['start'] = start;
      }
      if (highlightPositionsBySpanIdx[spanIdx]['end'] < end) {
        highlightPositionsBySpanIdx[spanIdx]['end'] = end;
      }
    }
  }

  return highlightPositionsBySpanIdx;
};

// MAIN
const getTextToHighlight = (spanElements, searchText) => {
  try {
    const searchWords = getSearchWordsWithIndex(searchText);

    // Now store matches by sequence position instead of by word
    const possibleMatches = getPossibleMatches(spanElements, searchWords);

    console.log('[getTextToHighlight] possibleMatches: ', possibleMatches);

    const closestMatch = findClosestMatch(searchWords, possibleMatches);

    console.log('[getTextToHighlight] closestMatch: ', closestMatch);

    const highlightPositionsBySpanIdx = getHighlightPositions(closestMatch);

    return highlightPositionsBySpanIdx;
  } catch (error) {
    console.error('[getTextToHighlight] error: ', error);
    return {};
  }
};

const highlightText = async (spanElements, textToHighlight = {}) => {
  if (!Object.keys(textToHighlight)?.length) return;

  for (const spanIdx of Object.keys(textToHighlight)) {
    const spanElement = spanElements[spanIdx];
    const { start, end } = textToHighlight[spanIdx];

    // add <mark> to the first and </mark> to the last character of the span element
    const result =
      spanElement.textContent.slice(0, start) +
      `<mark>${spanElement.textContent.slice(start, end)}</mark>` +
      spanElement.textContent.slice(end);

    spanElement.innerHTML = result;
  }
};

export { getTextToHighlight, highlightText };